/* eslint-disable react-hooks/exhaustive-deps */
import LoadingContent from 'components/LoadingContent';
import { useFormik } from 'formik';
import React from 'react';
import TopProcessBar from 'screen/selectCollege/TopProcessBar';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { Container, Form, Row, Spinner, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import SelectInput from 'components/input/SelectInput';
import { occupationOptions } from 'components/helper/helper';
import TextInput from 'components/input/textInput';
import GroupRadioButton from 'components/input/GroupRadioButton';
import { debounce } from 'lodash';
import { getFuturePlan, updateFuturePlan } from 'app/highSchoolSlice/highSchoolSlice';
import Grid from '@mui/joy/Grid';
import { ModalCheckEducationFields } from 'screen/education/common/ModalCheckMissingFields';
import SearchIcon from '@mui/icons-material/Search';
import pageStatusService from 'services/pageStatusService';
import { PAGE_NAME } from 'constants/common';
import websocketService from 'services/websocketService';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';

// When do you plan to start college?
export const describesOptions = [
  { value: '2026-27', label: '2026-27' },
  { value: '2028', label: '2028' },
  { value: '2029 or beyond', label: '2029 or beyond' },
  { value: '4', label: 'Already a college student' },
];

/** Map legacy 2025-26 cycle answers onto 2026-27 options. */
export const migrateWhichBestDescribeYou = (value?: string | null): string => {
  if (!value) return '';
  switch (value) {
    case '1': // Applying as a first year student in 2025 or 2026
    case '2': // Planning to start college in 2027 → 2026-27
      return '2026-27';
    case '3': // Planning to start college in 2028 or beyond
      return '2028';
    default:
      return value;
  }
};

interface FuturePlanProp {
  career_interest: string;
  other_career_interest: string;
  highest_degree_intend_to_earn: string;
  which_best_describe_you: string;
}

const defaultValues: FuturePlanProp = {
  career_interest: '',
  other_career_interest: '',
  highest_degree_intend_to_earn: '',
  which_best_describe_you: '',
};

interface Props { }

const FuturePlan = (props: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);
  const [showMissingFieldsModal, setShowMissingFieldsModal] = React.useState(false);
  const [reviewedAndComplete, setReviewedAndComplete] = React.useState(false);
  const highSchool = useAppSelector((store) => store.highSchool);
  const dispatch = useAppDispatch();
  const history = useHistory();
  let studentId = getUserId();
  let token = getAccessToken();
  const query = new URLSearchParams(useLocation().search);
  const check = query.get('check');
  const formik = useFormik({
    initialValues: defaultValues,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        const newValues = {
          ...values,
          student_id: studentId,
        };
        await dispatch(updateFuturePlan(newValues));
        setSaving(false);
      } catch (ex) {
        setSaving(false);
        console.log('Error:', ex);
      }
    },
  });
  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const handleContinue = () => {
    formik.handleSubmit();
    history.push(routers.generalCourse.path);
  };

  const getDetail = async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      setLoading(true);
      const pageStatus: {
        student_completed: boolean;
      } = await pageStatusService.getPageStatusByPageName(studentId, PAGE_NAME.FUTURE_PLANS);
      if(pageStatus) {
        setReviewedAndComplete(pageStatus.student_completed);
      }
      const res = await dispatch(getFuturePlan(studentId));
      if (res.payload) {
        const migratedDescribe = migrateWhichBestDescribeYou(res.payload.which_best_describe_you);
        const nextValues = {
          ...formik.values,
          ...res.payload,
          which_best_describe_you: migratedDescribe,
        };
        formik.setValues(nextValues);
        // Persist remapped cycle answer (e.g. "Planning to start in 2027" → 2026-27)
        if (
          res.payload.which_best_describe_you &&
          res.payload.which_best_describe_you !== migratedDescribe
        ) {
          await dispatch(
            updateFuturePlan({
              ...nextValues,
              student_id: studentId,
            })
          );
        }
      }
      setLoading(false);
      formik.setStatus('idle');
    } else {
      handleLogout();
    }
  };

  React.useEffect(() => {
    getDetail();
  }, []);
  
  // Auto save function ==========
  React.useEffect(() => {
    formik.dirty && debounceSaving();
  }, [formik.values]);
  const debounceSaving = React.useCallback(
    debounce(() => {
      if (!loading) formik.handleSubmit();
    }, 1500),
    []
  );

  React.useEffect(() => {
    // Handle responsive detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Auto save function ==========

  const handleCheckMissingFields = () => {
    setShowMissingFieldsModal(true);
  };

  const onReviewdAndComplete = () => { 
    const newValue = !reviewedAndComplete;
    setReviewedAndComplete(newValue);
    websocketService.finishReviewAndComplete(PAGE_NAME.FUTURE_PLANS, newValue);
  }

  return loading ? (
    <LoadingContent />
  ) : (
    <>
      <TopProcessBar
        stepProcess={[
          { percentOnProcess: 30, title: 'Best Describes Me', specialId: 'BestDescribesMe'},
          { percentOnProcess: 60, title: 'Career Interest', specialId: 'CareerInterest'},
          { percentOnProcess: 90, title: 'Highest Degree Intended', specialId: 'HighestDegreeIntended'},
        ]}
        title="Education"
        process={1}
        icon={<StickyNote2OutlinedIcon />}
        isMobile={isMobile}
      />

      <Container 
        className={`main-container gm-basic-info-page ${isMobile ? 'p-mobile-2' : ''}`}
        style={{
          paddingLeft: isMobile ? '12px' : undefined,
          paddingRight: isMobile ? '12px' : undefined,
          paddingTop: isMobile ? '60px' : undefined,
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <div className={`gm-section-card ${isMobile ? 'p-mobile-2' : 'px-4 py-4'}`}>
          <Form
            style={{ 
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%'
            }}
            onSubmit={formik.handleSubmit}
            className="gm-form-grid"
          >
            <div className={`gm-section-title ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600 }}>
                Future Plans
              </span>
            </div>
            <div className={`text-dart ${isMobile ? 'fs-6' : 'fs-5'} p-1 ${isMobile ? 'mb-2' : 'mb-4'}`}>
              Don't worry, this is not set in stone. They just want to know your future interest.
              Colleges will not hold you to this.
            </div>
            <div id="BestDescribesMe"></div>
            <Grid container spacing={isMobile ? 1 : 2} className="gm-form-fields">
              <Grid xs={12}>
                <GroupRadioButton
                  verticalLayout={true}
                  name="which_best_describe_you"
                  onChange={formik.handleChange}
                  values={formik.values.which_best_describe_you}
                  mdCol={12}
                  logo="span-cm-logo text-field-dark"
                  label="When do you plan to start college?*"
                  options={describesOptions}
                  isMissing={!formik.values.which_best_describe_you && check}
                  disabled={reviewedAndComplete}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <SelectInput
                  id="CareerInterest"
                  logo="span-cm-logo"
                  form={formik}
                  option={occupationOptions}
                  name="career_interest"
                  values={formik.values.career_interest}
                  label="Career Interest *"
                  isSatTest={true}
                  onChange={formik.handleChange}
                  isMissing={
                    (!formik.values.career_interest ||
                      formik.values.career_interest === 'Choose an option') &&
                    check
                  }
                  disabled={reviewedAndComplete}
                />
              </Grid>

              {formik.values.career_interest === 'Other' && (
                <Grid xs={12} md={6}>
                  <TextInput
                    countText={100}
                    values={formik.values.other_career_interest}
                    onChange={formik.handleChange}
                    label="Other career interest *"
                    name="other_career_interest"
                    isMissing={!formik.values.other_career_interest && check}
                    disabled={reviewedAndComplete}
                  />
                </Grid>
              )}

              <Grid xs={12}>
                <SelectInput
                  id="HighestDegreeIntended"
                  logo="span-cm-logo"
                  form={formik}
                  option={[
                    { value: `Associate's (AA,AS)` },
                    { value: `Bachelor's (BA,BS)` },
                    { value: `Master's (MA,MS)` },
                    { value: 'Business (MBA, MAcc)' },
                    { value: 'Law (JD, LLM)' },
                    { value: 'Medicine (MD,DO,DVM,DDS)' },
                    { value: 'Doctorate (PhD,EdD, etc)' },
                    { value: 'Other' },
                    { value: 'Undecided' },
                  ]}
                  name="highest_degree_intend_to_earn"
                  values={formik.values.highest_degree_intend_to_earn}
                  label="Highest Degree you intend to earn *"
                  isSatTest={true}
                  onChange={formik.handleChange}
                  isMissing={!formik.values.highest_degree_intend_to_earn && check}
                  disabled={reviewedAndComplete}
                />
              </Grid>
            </Grid>

            <div className={`gm-form-actions border-top ${isMobile ? 'pt-2 mt-3' : 'pt-3 mt-4'} d-flex ${isMobile ? 'flex-column' : 'justify-content-between align-items-center'}`}>
              {/* Icon + Checkbox combo on the left */}
              <div className={`${isMobile ? 'w-100 mb-3' : ''} d-flex justify-content-center`}>
                <div className="d-flex flex-column align-items-center">
                  <div
                    onClick={handleCheckMissingFields}
                    className="d-flex align-items-center cursor-pointer mb-2"
                  >
                    <SearchIcon
                      style={{
                        fontSize: '24px',
                        color: '#6c757d',
                      }}
                    />
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      check missing values
                    </span>
                  </div>

                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input
                      type="checkbox"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #6c757d',
                        borderRadius: '2px',
                      }}
                      checked={reviewedAndComplete}
                      onClick={onReviewdAndComplete}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? '14px' : '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Reviewed & Complete!
                    </span>
                  </div>
                </div>
              </div>

              {/* Save and Continue buttons on the right */}
              <div className={`${isMobile ? 'w-100' : 'col col-md-6'} text-center ${isMobile ? '' : 'text-md-end'}`}>
                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'flex-row'} justify-content-center`}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5 mx-2'} gm-btn-save`}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {saving ? <Spinner size="sm" animation="border" variant="light" /> : 'Save'}
                  </Button>
                  <Button 
                    variant="primary" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5'} gm-btn-continue`} 
                    onClick={() => handleContinue()}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Container>
      
      {/* Missing Fields Validation Modal */}
      <ModalCheckEducationFields
        show={showMissingFieldsModal}
        onClose={() => setShowMissingFieldsModal(false)}
        data={formik.values}
        page="futureplan"
      />
    </>
  );
};

export default FuturePlan;
